import type { Task } from "@/sdk/agent/task";

import { isChatTask } from "@/sdk/agent/chat";

import type { ChatContactAttributes, ValidatedChatContactAttributes } from "../types";

import AmazonConnectChatContact from "../AmazonConnectChatContact";
import { REQUIRED_CONTACT_ATTRIBUTES } from "../types";

/**
 * Type guard to check if a task is an Amazon Connect chat contact.
 * This combines the generic chat task check with the Amazon Connect specific type check for typescript niceness
 */
export function isAmazonConnectChatTask(task: Task): task is AmazonConnectChatContact {
    return isChatTask(task) && task instanceof AmazonConnectChatContact;
}

/**
 * Asserts that a task is an Amazon Connect chat contact.
 * Throws a TypeError if the assertion fails.
 * @param task - The task to check.
 * @param context - Optional context string for error messages.
 * @param message - Optional custom error message.
 * @throws {TypeError} If the task is not an Amazon Connect chat contact.
 */
export function assertAmazonConnectChatTask(
    task: Task,
    context?: string,
    message?: string,
): asserts task is AmazonConnectChatContact {
    if (isAmazonConnectChatTask(task)) return;
    const errorMsg = message ?? `task is not an Amazon Connect chat contact, got: ${task?.type}`;
    throw new TypeError(context ? `[${context}] ${errorMsg}` : errorMsg);
}

/**
 * Runtime check to determine if a Connect contact is a chat contact.
 * Note: This is NOT a true type guard - both chat and voice contacts are `connect.Contact` types.
 */
export function isChatContact(contact: connect.Contact): boolean {
    return contact.getType() === connect.ContactType.CHAT;
}

/**
 * A type guard that asserts that required contact attributes are present and have a `.value`.
 * narrow the loose ChatContactAttributes to the `ValidatedChatContactAttributes` type
 * to guarantee the presence of required fields and remove the need for non-null assertions.
 *
 * @param attributes The contact attributes to validate.
 * @param contactId The ID of the contact, for logging.
 * @throws {Error} If any required fields are missing.
 */
export function assertRequiredContactAttributes(
    attributes: ChatContactAttributes,
    contactId: string,
): asserts attributes is ValidatedChatContactAttributes {
    const missingFields = REQUIRED_CONTACT_ATTRIBUTES.filter((field) => !attributes[field]?.value);

    if (missingFields.length > 0) {
        throw new Error(
            `Missing required contact attributes for Amazon Connect chat contact ${
                contactId || "unknown"
            }: ${missingFields.join(", ")}`,
        );
    }
}
